import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { db } from "../firebaseConfig";
import {
    doc,
    onSnapshot,
    setDoc,
    serverTimestamp,
} from "firebase/firestore";

import { mplTokenMetadata, fetchAllDigitalAssetByOwner } from "@metaplex-foundation/mpl-token-metadata";
// import { fetchAssetsByOwner } from "@metaplex-foundation/mpl-core";
import {fetchAllTokenByOwner} from '@metaplex-foundation/mpl-toolbox'
import { useWallet } from "@solana/wallet-adapter-react";
import { createUmi } from "@metaplex-foundation/umi-bundle-defaults";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Connection } from "@solana/web3.js";
import "./SimulacrumExplorerStyle.css";
import ImageModal from "./ImageModal";
import { base58 } from "@metaplex-foundation/umi/serializers";
// Import additional functions for burning.
import { burnV1, findMetadataPda, TokenStandard } from "@metaplex-foundation/mpl-token-metadata";
import { burn, fetchAsset, collectionAddress, fetchCollection, fetchAssetsByOwner } from "@metaplex-foundation/mpl-core";
import { publicKey } from "@metaplex-foundation/umi";
import { walletAdapterIdentity } from "@metaplex-foundation/umi-signer-wallet-adapters";
import {
    mplBubblegum,
    getAssetWithProof,
    burnV2,
} from "@metaplex-foundation/mpl-bubblegum";
import { dasApi } from '@metaplex-foundation/digital-asset-standard-api';
import * as htmlToImage from "html-to-image";
import { getDomainKeySync, NameRegistryState } from "@bonfida/spl-name-service";
import VideoNFTPlayerComponent from "./VideoNFTPlayerComponent";
import AudioNFTPlayerComponent from "./AudioNFTPlayerComponent";

const FREE_STYLES = [
    { id: "og-retro", label: "OG Retro" },
    { id: "modern-dark", label: "Modern Dark" },
    { id: "modern-light", label: "Modern Light" },
    { id: "vaporwave", label: "Vaporwave" },

];

const PREMIUM_STYLES = [
    { id: "neo-brutalism", label: "Neo Brutalism" },
    { id: "win95", label: "Windows 95" },
    { id: "matrix", label: "Matrix" },
    { id: "gameboy", label: "Game Boy" },
    { id: "pokedex", label: "Pokédex" },
];


function WalletExplorer({ publicKey: propPublicKey }) {
    const {walletAddress} = useParams();
    const wallet = useWallet();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [burnInfo, setBurnInfo] = useState(null); // For displaying burn transaction details.
    const tokensPerPage = 10;
    const [isZoomed, setIsZoomed] = useState(false);
    // Determine which address to use.
// 🧭 Address used for *viewing* tokens (the searched wallet or connected wallet)
    const viewAddress =
        propPublicKey || walletAddress || wallet.publicKey?.toBase58();

// 🧑‍💻 Address used for *style ownership* (who bought the styles)
// This is the connected wallet if available, otherwise we fall back to viewAddress.
    const styleOwnerAddress = wallet.publicKey?.toBase58() || viewAddress;
    const [activeIndex, setActiveIndex] = useState(null);
    const [modalImage, setModalImage] = useState(null);
    const [showCompressed, setShowCompressed] = useState(true);
    const [mediaFilter, setMediaFilter] = useState("all");
    // if (!walletAddress && !propPublicKey && viewAddress) {
    //     navigate(`/account/${viewAddress}`);
    // }
    const [styleMode, setStyleMode] = useState(
        () => localStorage.getItem("walletExplorerStyle") || "og-retro"
    );
    const queryClient = useQueryClient();

    const [unlockedStyles, setUnlockedStyles] = useState(
        () => [...FREE_STYLES.map((s) => s.id)]
    );
    const [sharingTokenMint, setSharingTokenMint] = useState(null);
    const cardRefs = useRef({});
    const walletAddressStr = wallet.publicKey?.toBase58() || null;
    const canBurnThisWallet =
        walletAddressStr && viewAddress && walletAddressStr === viewAddress;
    const [activeTab, setActiveTab] = useState("explorer"); // "explorer" | "audio" | "video"
    // Audio tab state
    const [audioSearch, setAudioSearch] = useState("");
    const [audioActiveIndex, setAudioActiveIndex] = useState(0);
    const [audioShuffle, setAudioShuffle] = useState(false);
    const [audioRepeatOne, setAudioRepeatOne] = useState(false);

    const [videoSearch, setVideoSearch] = useState("");
    const [videoActiveIndex, setVideoActiveIndex] = useState(0);
    const [videoShuffle, setVideoShuffle] = useState(false);
    const [videoRepeatOne, setVideoRepeatOne] = useState(false);
    const videoRef = useRef(null);
    const audioRef = useRef(null);

    const safeName = (t) => (t?.name || "Unknown").toString();
    // ---- DAS paging behavior ----
    const DAS_PAGE_SIZE = 1000;          // keep 1000
    const DAS_AUTO_PAGES = 10;           // auto-load first 10 pages (10k)
    const [dasHasMore, setDasHasMore] = useState(false);
    const [dasNextPage, setDasNextPage] = useState(2);
    const [dasLoadingMore, setDasLoadingMore] = useState(false);

    useEffect(() => {
        setCurrentPage(1);
    }, [showCompressed, mediaFilter, searchTerm]); // searchTerm added

    useEffect(() => {
        const owner = styleOwnerAddress;
        if (!owner) return;

        const userDocRef = doc(db, "user_styles", owner);
        const unsubscribe = onSnapshot(userDocRef, (snap) => {
            const data = snap.data();
            const purchased = Array.isArray(data?.styles) ? data.styles : [];
            // Always include the free styles for whoever is viewing:
            setUnlockedStyles([
                ...FREE_STYLES.map((s) => s.id),
                ...purchased,
            ]);
        });

        return () => unsubscribe();
    }, [styleOwnerAddress]);


    useEffect(() => {
        const owner = styleOwnerAddress;
        if (!owner) return;

        const userDocRef = doc(db, "user_styles", owner);
        // Fire-and-forget: if doc doesn't exist, create one with just free styles
        (async () => {
            const snap = await import("firebase/firestore").then(({getDoc}) =>
                getDoc(userDocRef)
            );
            if (!snap.exists()) {
                await setDoc(userDocRef, {
                    styles: [],             // only purchased styles stored here
                    createdAt: serverTimestamp(),
                });
            }
        })();
    }, [styleOwnerAddress]);


// Resolve a .sol SNS domain to its owner public key using direct lookup
    const getPublicKeyFromSolDomain = async (domain) => {
        const rpc = process.env.REACT_APP_RPC_ENDPOINT;
        if (!rpc) throw new Error("RPC endpoint is not configured.");

        const connection = new Connection(rpc);

        const cleanDomain = domain.trim().toLowerCase().endsWith(".sol")
            ? domain.trim().slice(0, -4)
            : domain.trim();

        const {pubkey} = getDomainKeySync(cleanDomain);
        const {registry} = await NameRegistryState.retrieve(connection, pubkey);

        const owner = registry.owner.toBase58();
        console.log("[SNS] Resolved", domain, "->", owner);
        return owner;
    };


    const handleStyleChange = (mode) => {
        setStyleMode(mode);
        try {
            localStorage.setItem("walletExplorerStyle", mode);
        } catch (e) {
            // Ignore storage errors (e.g. private mode)
            console.warn("Could not persist style mode:", e);
        }
    };

    function TokenImage({image, name, setModalImage}) {
        const handleClick = () => {
            // Open the overlay with the selected image
            setModalImage({image, name});
        };

        return (
            <img
                className="token-item-image"
                src={image}
                alt={name}
                onClick={handleClick}
            />
        );
    }


    const generateTokenCardImage = async (token) => {
        const cardEl = cardRefs.current[token.mint];
        if (!cardEl) return null;

        const fileName = `${(token.name || "nft")
            .replace(/[^\w\-]+/g, "_")}-crumb.png`;

        const bg = window.getComputedStyle(cardEl).backgroundColor || null;

        // Use html-to-image to create a PNG blob of the card
        const blob = await htmlToImage.toBlob(cardEl, {
            backgroundColor: bg,
            pixelRatio: 2, // similar to scale: 2
        });

        if (!blob) {
            console.error("Failed to generate image blob from card.");
            return null;
        }

        const file = new File([blob], fileName, {type: "image/png"});

        return {file, blob, fileName};
    };


    const handleDownloadTokenCard = async (token) => {
        try {
            setSharingTokenMint(token.mint);
            const result = await generateTokenCardImage(token);
            if (!result) return;

            const {blob, fileName} = result;

            const urlObj = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = urlObj;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(urlObj);
        } catch (err) {
            console.error("Download failed:", err);
            // alert("Could not download card.");
        } finally {
            setSharingTokenMint(null);
        }
    };


    const handleCopyTokenCard = async (token) => {
        try {
            setSharingTokenMint(token.mint);
            const result = await generateTokenCardImage(token);
            if (!result) return;

            const {blob} = result;

            if (navigator.clipboard && window.ClipboardItem) {
                await navigator.clipboard.write([
                    new ClipboardItem({"image/png": blob}),
                ]);
                // alert("Card copied to clipboard!");
            } else {
                alert("Copying images is not supported on this browser/device.");
            }
        } catch (err) {
            console.error("Copy failed:", err);
            alert("Copy not supported on this device/browser.");
        } finally {
            setSharingTokenMint(null);
        }
    };


    const handleShareTokenCard = async (tokenObj, platform) => {
        const isMobile = /Android|iPhone|iPad|iPod/i.test(
            navigator.userAgent || ""
        );

        try {
            setSharingTokenMint(tokenObj.mint);

            // Reuse the html-to-image helper
            const result = await generateTokenCardImage(tokenObj);
            if (!result) return;

            const {blob, file, fileName} = result;

            // You can change this to the detail URL if you prefer:
            // const pageUrl = `${window.location.origin}/token/${tokenObj.mint}`;
            const pageUrl = window.location.href;

            const shareText =
                `Viewing "${tokenObj.name || "this NFT"}" in The Crumb – ` +
                `NFT Wallet Explorer.`;
            const encodedText = encodeURIComponent(shareText);
            const encodedUrl = encodeURIComponent(pageUrl);

            // --- MOBILE: Web Share API with file (ideal for TWA / phones) ---
            if (
                isMobile &&
                navigator.canShare &&
                navigator.canShare({files: [file]})
            ) {
                await navigator.share({
                    files: [file],
                    title: tokenObj.name || "My NFT",
                    text: `${shareText} ${pageUrl}`,
                });
                return;
            }

            // --- DESKTOP / fallback: download + open social composer ---

            // 1) Download the image so they can attach it manually
            const urlObj = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = urlObj;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(urlObj);

            // 2) Open the appropriate social network with prefilled text/url
            let socialUrl = null;
            switch (platform) {
                case "twitter":
                    socialUrl =
                        `https://twitter.com/intent/tweet?text=${encodedText}` +
                        `&url=${encodedUrl}`;
                    break;
                case "facebook":
                    socialUrl =
                        `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}` +
                        `&quote=${encodedText}`;
                    break;
                case "threads":
                    socialUrl =
                        `https://www.threads.net/intent/post?text=${encodedText}%20${encodedUrl}`;
                    break;
                case "instagram":
                    // No official web intent – just open Instagram
                    socialUrl = "https://www.instagram.com/";
                    break;
                default:
                    break;
            }

            if (socialUrl) {
                window.open(socialUrl, "_blank", "noopener,noreferrer");
            }
        } catch (err) {
            console.error("Error sharing token card:", err);
            // Optional: alert if you want user feedback
            // alert("Could not generate a shareable image for this token.");
        } finally {
            setSharingTokenMint(null);
        }
    };


    // Helper to send a transaction and wait for confirmation using Umi.
    const sendTxWithConfirmation = async (txBuilder, umi) => {
        // Umi will send and confirm the transaction against the same RPC.
        const {signature} = await txBuilder.sendAndConfirm(umi);
        // `signature` is a Uint8Array. Convert it → base58 string.
        const [txId] = base58.deserialize(signature);

        console.log("Transaction sent and confirmed with txId:", txId);

        return txId;
    };

    // Concurrency-limited parallel runner
    const runPool = async (limit, items, worker) => {
        const results = new Array(items.length);
        let i = 0;

        const runners = new Array(Math.min(limit, items.length)).fill(0).map(async () => {
            while (true) {
                const idx = i++;
                if (idx >= items.length) return;
                try {
                    results[idx] = await worker(items[idx], idx);
                } catch (e) {
                    results[idx] = null;
                }
            }
        });

        await Promise.all(runners);
        return results;
    };

    // Concurrency-limited runner that streams results as they complete
    const runPoolStream = async (limit, items, worker, onResult) => {
        let i = 0;

        const runners = new Array(Math.min(limit, items.length)).fill(0).map(async () => {
            while (true) {
                const idx = i++;
                if (idx >= items.length) return;

                try {
                    const res = await worker(items[idx], idx);
                    if (res && onResult) onResult(res, idx);
                } catch (e) {
                    // ignore per-item errors
                }
            }
        });

        await Promise.all(runners);
    };

// Cache JSON fetches by URI (localStorage + in-memory)
    const __jsonCacheMem = new Map();
    const fetchJsonCached = async (uri) => {
        if (!uri) return null;

        if (__jsonCacheMem.has(uri)) return __jsonCacheMem.get(uri);

        const cacheKey = `json_${uri}`;
        const cacheExpiry = 7 * 24 * 60 * 60 * 1000; // 7 days

        try {
            const cached = localStorage.getItem(cacheKey);
            if (cached) {
                const parsed = JSON.parse(cached);
                const { data, timestamp } = parsed;

                if (Date.now() - timestamp < cacheExpiry) {
                    __jsonCacheMem.set(uri, data);
                    return data;
                }

            }
        } catch (e) {
            // ignore cache parse errors
        }

        try {
            const res = await fetch(uri, {cache: "force-cache"});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();

            __jsonCacheMem.set(uri, data);
            try {
                localStorage.setItem(cacheKey, JSON.stringify({data, timestamp: Date.now()}));
            } catch (e) {
                // ignore quota errors
            }

            return data;
        } catch (e) {
            __jsonCacheMem.set(uri, null);
            return null;
        }
    };


    const fetchUserTokens = async (address) => {
        if (!address) throw new Error("Wallet address not provided.");

        const originalAddress = String(address).trim();
        let ownerAddress = originalAddress;

        // Resolve SNS .sol -> wallet address
        if (originalAddress.toLowerCase().endsWith(".sol")) {
            ownerAddress = await getPublicKeyFromSolDomain(originalAddress);
            console.log("[SNS] Resolved", originalAddress, "->", ownerAddress);
        }

        const cacheKey = `tokens_${ownerAddress}`;
        const cacheExpiry = 86400000; // 24 hours


        const cached = localStorage.getItem(cacheKey);
        if (cached) {
            const parsed = JSON.parse(cached);
            const { data, timestamp } = parsed;

            if (Date.now() - timestamp < cacheExpiry) {
                console.log("Using cached data");

                // ✅ Keep query key consistent with useQuery(["tokens", viewAddress])
                queryClient.setQueryData(["tokens", originalAddress], data);

                // ✅ Restore "Load more" state from cache
                const paging = parsed?.dasPaging;
                setDasHasMore(!!paging?.hasMore);
                setDasNextPage(Number(paging?.nextPage || 2));

                return data;
            }
        }


        const rpcEndpoint = process.env.REACT_APP_RPC_ENDPOINT;
        if (!rpcEndpoint) throw new Error("RPC endpoint is not configured.");

        // --- DAS: fetch ALL assets by owner (paginated) ---
        const fetchDASPage = async (page) => {
            const body = {
                jsonrpc: "2.0",
                id: "get-assets-by-owner",
                method: "getAssetsByOwner",
                params: {
                    ownerAddress: ownerAddress,
                    page,
                    limit: DAS_PAGE_SIZE,
                },
            };

            const res = await fetch(rpcEndpoint, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify(body),
            });

            const json = await res.json();

            if (json?.error) {
                console.error("[DAS] getAssetsByOwner error:", json.error);
                throw new Error(json.error.message || "DAS getAssetsByOwner failed");
            }

            const items = json?.result?.items || [];
            const total = json?.result?.total;               // some DAS implementations provide this
            const cursor = json?.result?.cursor || json?.result?.paginationToken; // some provide one of these

            return { items, total, cursor };
        };

        // const allAssets = [];
        let page = 1;


        // --- Map DAS assets to your token shape ---
        const mapDASAssetToToken = async (asset) => {
            const assetId = asset?.id || null;
            if (!assetId) return null;

            const jsonUri = asset?.content?.json_uri || null;

            // Best-effort fields from DAS (fast path, no extra fetch)
            let metadata = asset?.content?.metadata || null;
            let imageUrl = asset?.content?.links?.image || metadata?.image || null;
            let externalUrl = metadata?.external_url || asset?.content?.links?.external || null;

            // Collection/grouping (DAS often uses `grouping`)
            const collectionKey =
                asset?.grouping?.find((g) => g?.group_key === "collection")?.group_value ||
                asset?.collection?.key ||
                null;

            let attributes = metadata?.attributes || [];

            // If we’re missing important stuff, hydrate from json_uri (slow path)
            const needsHydrate =
                !!jsonUri &&
                (
                    !metadata ||
                    !imageUrl ||
                    !attributes ||
                    (Array.isArray(attributes) && attributes.length === 0) ||

                    // ✅ NEW: hydrate if we’re missing media info
                    (
                        !metadata?.animation_url &&
                        !(
                            metadata?.properties &&
                            Array.isArray(metadata.properties.files) &&
                            metadata.properties.files.length
                        ) &&
                        !(
                            Array.isArray(asset?.content?.files) &&
                            asset.content.files.length
                        )
                    )
                );
            if (needsHydrate) {
                const hydrated = await fetchJsonCached(jsonUri);
                if (hydrated) {
                    // ✅ Merge so we keep DAS fields but also gain animation_url/files/category
                    metadata = metadata
                        ? {
                            ...hydrated,
                            ...metadata,
                            properties: {
                                ...(hydrated.properties || {}),
                                ...(metadata.properties || {}),
                                files: [
                                    ...((hydrated.properties && Array.isArray(hydrated.properties.files)) ? hydrated.properties.files : []),
                                    ...((metadata.properties && Array.isArray(metadata.properties.files)) ? metadata.properties.files : []),
                                ],
                            },
                        }
                        : hydrated;

                    imageUrl = imageUrl || hydrated?.image || null;
                    externalUrl = externalUrl || hydrated?.external_url || null;
                    attributes =
                        (attributes && attributes.length ? attributes : hydrated?.attributes) || [];
                }
            }

// --- Media parsing (DAS + hydrated json_uri) ---

// DAS sometimes provides files here (with mime/type)
            const dasFiles = Array.isArray(asset?.content?.files) ? asset.content.files : [];

// Metadata files (classic token-metadata)
            const metaFiles =
                (metadata?.properties && Array.isArray(metadata.properties.files))
                    ? metadata.properties.files
                    : [];

            const allFiles = [...dasFiles, ...metaFiles];

            const getMime = (f) => ((f?.mime || f?.type || "") + "").toLowerCase();
            const getUri = (f) => f?.uri || f?.url || null;

            const imageFile = allFiles.find((f) => getMime(f).startsWith("image/")) || null;
            const videoFile = allFiles.find((f) => getMime(f).startsWith("video/")) || null;
            const audioFile = allFiles.find((f) => getMime(f).startsWith("audio/")) || null;

            let animationUrl =
                metadata?.animation_url ||
                getUri(videoFile) ||
                null;

            let audioUrl =
                getUri(audioFile) ||
                null;

// ✅ If animation_url is actually audio, treat it as audio
            if (animationUrl && !audioUrl) {
                const lower = String(animationUrl).toLowerCase();
                if (lower.includes("ext=mp3") || lower.endsWith(".mp3") || lower.endsWith(".wav") || lower.endsWith(".m4a") || lower.endsWith(".ogg")) {
                    audioUrl = animationUrl;
                    animationUrl = null;
                }
            }

// Use cover image even when video/audio exists
            const coverImage =
                imageUrl ||
                getUri(imageFile) ||
                null;

            const categoryRaw = (
                metadata?.properties?.category ||
                metadata?.category ||
                asset?.content?.metadata?.properties?.category ||
                asset?.content?.metadata?.category ||
                ""
            ).toLowerCase();

// Determine mediaType
            let mediaCategory = "";
            if (animationUrl || categoryRaw === "video" || videoFile) mediaCategory = "video";
            else if (audioUrl || categoryRaw === "audio" || audioFile) mediaCategory = "audio";
            else if (coverImage || categoryRaw === "image" || imageFile) mediaCategory = "image";


            // Type: compressed vs regular (still all from DAS)
            const iface = asset?.interface || "";
            const isCompressed = !!asset?.compression?.compressed;

            let tokenType = "NFT"; // Legacy default
            if (isCompressed || iface === "MplBubblegumV2") tokenType = "Compressed NFT";
            else if (iface === "MplCoreAsset") tokenType = "Core NFT";
            else tokenType = "NFT"; // legacy token-metadata bucket

            return {
                mint: assetId,
                amount: 1,
                type: tokenType,
                name: metadata?.name || asset?.content?.metadata?.name || "Unknown",
                description: metadata?.description || "",
                symbol: metadata?.symbol || asset?.content?.metadata?.symbol || "Unknown",
                uri: jsonUri || "No Metadata URI",
                collectionKey,
                image: coverImage || "No image URL",
                external_url: externalUrl || null,
                animationUrl: animationUrl || null,
                audioUrl: audioUrl || null,
                mediaType: mediaCategory,
                attributes: Array.isArray(attributes) ? attributes : [],
            };
        };


        const CONCURRENCY = 12; // try 12-20

// Streamed tokens (in the order they finish)
        const loadedTokens = [];
        const uniqueTokensMap = new Map();

// Push token as soon as it's ready (this is the "old behavior")
        const pushToken = (token) => {
            if (!token || !token.mint) return;

            if (!uniqueTokensMap.has(token.mint)) {
                uniqueTokensMap.set(token.mint, token);
                loadedTokens.push(token);

                // ✅ STREAM into React Query so UI updates immediately
                queryClient.setQueryData(["tokens", address], [...loadedTokens]);
            }
        };

        let reachedAutoLimit = false;
        let totalFromServer = null;
        let cursorFromServer = null;
        let lastFetchedCount = 0;

        while (true) {
            const { items, total, cursor } = await fetchDASPage(page);
            if (!items.length) break;

            lastFetchedCount = items.length;

            if (typeof total === "number") totalFromServer = total;
            if (cursor) cursorFromServer = cursor;

            await runPoolStream(CONCURRENCY, items, mapDASAssetToToken, (token) => {
                pushToken(token);
            });

            // Prefer "total" if provided
            if (typeof totalFromServer === "number") {
                const loadedSoFar = page * DAS_PAGE_SIZE;
                if (loadedSoFar >= totalFromServer) {
                    page += 1;
                    break;
                }
            } else if (!cursorFromServer) {
                // Fallback only if no total/cursor exists
                if (items.length < DAS_PAGE_SIZE) {
                    page += 1;
                    break;
                }
            }

            page += 1;

            if (page > DAS_AUTO_PAGES) {
                reachedAutoLimit = true;
                break;
            }
        }

// ✅ determine hasMore correctly
        const lastPageWasFull = cursorFromServer
            ? true // if cursor exists, assume more pages may exist until cursor stops
            : (typeof totalFromServer === "number"
                ? (page - 1) * DAS_PAGE_SIZE < totalFromServer
                : true); // fallback, but we’ll overwrite below

// ✅ Determine hasMore based on real signals
        let hasMore = false;

        if (typeof totalFromServer === "number") {
            // We fetched pages 1..(page-1). If that's still less than total, there are more.
            const loadedSoFar = (page - 1) * DAS_PAGE_SIZE;
            hasMore = loadedSoFar < totalFromServer;
        } else {
            // No total available => safest signal is "last page was full"
            hasMore = lastFetchedCount === DAS_PAGE_SIZE;
        }

        setDasHasMore(hasMore);
        setDasNextPage(page);



// ✅ Final pass: sort by collection once 100% complete
        const uniqueTokens = Array.from(uniqueTokensMap.values());

        uniqueTokens.sort((a, b) => {
            if (a.collectionKey && b.collectionKey) return a.collectionKey.localeCompare(b.collectionKey);
            if (a.collectionKey) return -1;
            if (b.collectionKey) return 1;
            return 0;
        });

// Replace streamed ordering with final collection ordering
        queryClient.setQueryData(["tokens", address], uniqueTokens);

        localStorage.setItem(
            cacheKey,
            JSON.stringify({
                data: uniqueTokens,
                timestamp: Date.now(),
                dasPaging: {
                    hasMore,
                    nextPage: page,
                    pageSize: DAS_PAGE_SIZE,
                },
            })
        );

        return uniqueTokens;

    };
        const mapCoreDigitalAssetToToken = async (asset) => {
            const mintAddress = asset.publicKey;
            const metadataUri = asset.uri || null;
            const collectionKey = asset.collection?.key || null;
            const amount = asset.amount;

            let metadata = null;
            let imageUrl = null;
            let externalUrl = null;

            if (metadataUri) {
                try {
                    const response = await fetch(metadataUri);
                    metadata = await response.json();
                    imageUrl = metadata?.image || null;
                    externalUrl = metadata?.external_url || null;
                } catch (error) {
                    // Ignore error.
                }
            }

            // --- Media parsing from metadata.properties.files ---
            const files = metadata?.properties?.files || [];
            const categoryRaw = metadata?.properties?.category || "";

            const findByMimePrefix = (prefix) => {
                if (!Array.isArray(files) || !files.length) return null;
                return (
                    files.find((f) => {
                        const mime = (f.type || f.mime || "").toLowerCase();
                        return mime.startsWith(prefix);
                    }) || null
                );
            };

            const imageFile = findByMimePrefix("image/");
            const videoFile = findByMimePrefix("video/");
            const audioFile = findByMimePrefix("audio/");

            // If no image yet, fall back to image file
            if (!imageUrl && imageFile && imageFile.uri) {
                imageUrl = imageFile.uri;
            }

            // Determine mediaCategory from category or from files
            let mediaCategory = categoryRaw.toLowerCase();
            if (!mediaCategory) {
                if (videoFile) mediaCategory = "video";
                else if (audioFile) mediaCategory = "audio";
                else if (imageFile) mediaCategory = "image";
                else mediaCategory = "";
            }

            // Derive animationUrl / audioUrl
            let animationUrl = null;
            let audioUrl = null;

            const rawAnimationUrl = metadata?.animation_url || null;

            if (mediaCategory === "video") {
                // Prefer explicit animation_url, then video file
                animationUrl =
                    rawAnimationUrl ||
                    (videoFile && videoFile.uri) ||
                    null;
                // Optional separate audio
                if (audioFile && audioFile.uri) {
                    audioUrl = audioFile.uri;
                }
            } else if (mediaCategory === "audio") {
                // Audio NFTs: audio URL only
                audioUrl =
                    rawAnimationUrl ||
                    (audioFile && audioFile.uri) ||
                    null;
            } else {
                // Image / unknown: derive from files if present
                if (videoFile && videoFile.uri) {
                    animationUrl = videoFile.uri;
                }
                if (!animationUrl && rawAnimationUrl) {
                    const lower = rawAnimationUrl.toLowerCase();
                    if (lower.includes("ext=mp4") || lower.endsWith(".mp4")) {
                        animationUrl = rawAnimationUrl;
                    } else if (lower.includes("ext=mp3") || lower.endsWith(".mp3")) {
                        audioUrl = rawAnimationUrl;
                    }
                }
                if (!audioUrl && audioFile && audioFile.uri) {
                    audioUrl = audioFile.uri;
                }
            }

            const attributes = Array.isArray(metadata?.attributes)
                ? metadata.attributes
                : [];

            return {
                mint: mintAddress,
                name: asset.name || metadata?.name || "Unknown",
                amount,
                type: "Core NFT",
                description: metadata?.description || "",
                symbol: asset.data?.symbol || metadata?.symbol || "Unknown",
                uri: metadataUri || "No Metadata URI",
                collectionKey: asset.updateAuthority?.address || collectionKey || null,
                image: imageUrl || "No image URL",
                external_url: externalUrl || null,
                // New fields to match compressed NFT behavior
                animationUrl: animationUrl || null,
                audioUrl: audioUrl || null,
                mediaType: mediaCategory, // "image", "video", "audio", or ""
                attributes,
            };
        };

        // const digitalAssetsTokens = await runPool(
        //     12,
        //     digitalAssets,
        //     async (asset) => {
        //         const token = await mapDigitalAssetToToken(asset);
        //         pushToken(token);
        //         return token;
        //     }
        // );
        //
        // const coreAssetsTokens = await runPool(
        //     12,
        //     coreDigitalAssets,
        //     async (asset) => {
        //         const token = await mapCoreDigitalAssetToToken(asset);
        //         pushToken(token);
        //         return token;
        //     }
        // );

        //     //
        //     // 2. COMPRESSED NFTS via DAS (raw JSON-RPC)
        //     //
        //     const mapCompressedAssetToToken = async (asset) => {
        //         const assetId = asset.id;
        //         const jsonUri = asset.content?.json_uri || null;
        //
        //         let metadata = asset.content?.metadata || null;
        //         let imageUrl = asset.content?.links?.image || null;
        //         let externalUrl =
        //             metadata?.external_url || asset.content?.links?.external || null;
        //
        //         // Try fetching JSON if needed
        //         // if (!metadata && jsonUri) {
        //         //     try {
        //         //         const response = await fetch(jsonUri);
        //         //         metadata = await response.json();
        //         //         if (!imageUrl) imageUrl = metadata.image || null;
        //         //         if (!externalUrl) externalUrl = metadata.external_url || null;
        //         //     } catch (e) {
        //         //         // Ignore metadata fetch error.
        //         //     }
        //         // }
        //
        //         // Decide if we need to fetch the full JSON metadata
        //         const needsRemoteMetadata =
        //             jsonUri &&
        //             (
        //                 !metadata ||                              // no metadata at all
        //                 !metadata.image ||                        // missing image field
        //                 (!metadata.animation_url &&               // missing animation_url
        //                     !(
        //                         metadata.properties &&
        //                         Array.isArray(metadata.properties.files) &&
        //                         metadata.properties.files.length
        //                     ))                                   // and no files[] array
        //             );
        //
        //         if (needsRemoteMetadata) {
        //             try {
        //                 const response = await fetch(jsonUri);
        //                 const fetchedMetadata = await response.json();
        //
        //                 // Merge any partial DAS metadata with the full fetched metadata
        //                 metadata = metadata
        //                     ? { ...metadata, ...fetchedMetadata }
        //                     : fetchedMetadata;
        //
        //                 if (!imageUrl) imageUrl = fetchedMetadata.image || null;
        //                 if (!externalUrl) externalUrl = fetchedMetadata.external_url || null;
        //             } catch (e) {
        //                 // Ignore metadata fetch error.
        //             }
        //         }
        //
        //
        //         // Collect files from metadata or DAS content
        //         const filesFromMetadata = metadata?.properties?.files || [];
        //         const filesFromContent = asset.content?.files || [];
        //         const allFiles = [...filesFromMetadata, ...filesFromContent];
        //
        //         // Prefer explicit animation_url if present
        //         let animationUrl =
        //             metadata?.animation_url ||
        //             asset.content?.links?.animation_url ||
        //             null;
        //
        //         // If no animationUrl yet, look for a video file
        //         if (!animationUrl && allFiles.length) {
        //             const videoFile = allFiles.find((f) => {
        //                 const mime = (f.type || f.mime || "").toLowerCase();
        //                 return mime.startsWith("video/");
        //             });
        //             if (videoFile) {
        //                 animationUrl = videoFile.uri || null;
        //             }
        //         }
        //
        //         // Look for an audio file, if any
        //         let audioUrl = null;
        //         if (allFiles.length) {
        //             const audioFile = allFiles.find((f) => {
        //                 const mime = (f.type || f.mime || "").toLowerCase();
        //                 return mime.startsWith("audio/");
        //             });
        //             if (audioFile) {
        //                 audioUrl = audioFile.uri || null;
        //             }
        //         }
        //
        //         // Fallback to files[] if still no image
        //         if (!imageUrl && allFiles.length) {
        //             const imageFile =
        //                 allFiles.find((f) => {
        //                     const mime = (f.type || f.mime || "").toLowerCase();
        //                     return mime.startsWith("image/");
        //                 }) || allFiles[0];
        //             imageUrl = imageFile?.uri || null;
        //         }
        //
        //
        //         const collectionGrouping =
        //             asset.grouping?.find((g) => g.group_key === "collection") || null;
        //
        //         // Read media category directly from metadata.properties.category
        //         const mediaCategory =
        //             (metadata?.properties?.category ||
        //                 asset.content?.metadata?.properties?.category ||
        //                 asset.content?.metadata?.category ||
        //                 "").toLowerCase();
        //
        //         const baseDescription =
        //             metadata?.description ||
        //             asset.content?.metadata?.description ||
        //             "";
        //
        //         const attributes =
        //             Array.isArray(metadata?.attributes)
        //                 ? metadata.attributes
        //                 : Array.isArray(asset.content?.metadata?.attributes)
        //                 ? asset.content.metadata.attributes
        //                 : [];
        //
        //         return {
        //             mint: assetId,
        //             assetId,
        //             name:
        //                 metadata?.name ||
        //                 asset.content?.metadata?.name ||
        //                 "Unknown",
        //             amount: 1,
        //             type: "Compressed NFT",
        //             description: baseDescription,
        //             mediaType: mediaCategory, // <-- just the category, e.g. "image", "video", "audio"
        //             symbol:
        //                 metadata?.symbol ||
        //                 asset.content?.metadata?.symbol ||
        //                 "Unknown",
        //             uri: jsonUri || "No Metadata URI",
        //             collectionKey: collectionGrouping?.group_value || null,
        //             image: imageUrl || "No image URL",
        //             external_url: externalUrl || null,
        //             // New fields for richer media rendering
        //             animationUrl: animationUrl || null,
        //             audioUrl: audioUrl || null,
        //             attributes,
        //         };
        //
        //     };
        //
        //     const fetchCompressedTokensForOwner = async (ownerAddress, pushToken) => {
        //         let compressedTokens = [];
        //
        //         try {
        //             const dasBody = {
        //                 jsonrpc: "2.0",
        //                 id: "getAssetsByOwner",
        //                 method: "getAssetsByOwner",
        //                 params: {
        //                     ownerAddress,
        //                     page: 1,
        //                     limit: 1000,
        //                 },
        //             };
        //
        //             const resp = await fetch(process.env.REACT_APP_RPC_ENDPOINT, {
        //                 method: "POST",
        //                 headers: { "Content-Type": "application/json" },
        //                 body: JSON.stringify(dasBody),
        //             });
        //
        //             const json = await resp.json();
        //
        //             if (json.error) {
        //                 console.error("DAS getAssetsByOwner error:", json.error);
        //             } else {
        //                 const rpcAssetList = json.result || {};
        //                 console.log(
        //                     "DAS getAssetsByOwner items:",
        //                     rpcAssetList.items?.length ?? 0
        //                 );
        //
        //                 const cnftAssets = (rpcAssetList.items || []).filter(
        //                     (asset) => asset.compression?.compressed
        //                 );
        //
        //                 for (const asset of cnftAssets) {
        //                     const token = await mapCompressedAssetToToken(asset);
        //                     compressedTokens.push(token);
        //                     if (pushToken) pushToken(token);
        //                 }
        //             }
        //         } catch (e) {
        //             console.error("Error fetching compressed NFTs via DAS:", e);
        //             compressedTokens = [];
        //         }
        //
        //         return compressedTokens;
        //     };
        //
        //
        //     const compressedTokens = fetchCompressedTokensForOwner(ownerAddress, pushToken);
        //
        //
        //     //
        //     // 3. MERGE + DEDUPE + SORT (same as before, with cNFTs added)
        //     //
        //     const combinedTokens = [
        //         ...digitalAssetsTokens,
        //         ...coreAssetsTokens,
        //         ...compressedTokens,
        //     ];
        //
        //     const uniqueTokens = combinedTokens.reduce((acc, token) => {
        //         if (!acc.some((t) => t.mint === token.mint)) {
        //             acc.push(token);
        //         }
        //         return acc;
        //     }, []);
        //
        //     uniqueTokens.sort((a, b) => {
        //         if (a.collectionKey && b.collectionKey) {
        //             return a.collectionKey.localeCompare(b.collectionKey);
        //         }
        //         if (a.collectionKey) return -1;
        //         if (b.collectionKey) return 1;
        //         return 0;
        //     });
        //
        //     localStorage.setItem(
        //         cacheKey,
        //         JSON.stringify({ data: uniqueTokens, timestamp: Date.now() })
        //     );
        //
        //     return uniqueTokens;
        // };
        //
        //
        //
        //
        //


    const {data: tokens = [], isLoading, refetch, error} = useQuery({
            queryKey: ["tokens", viewAddress],
            queryFn: () => fetchUserTokens(viewAddress),
            staleTime: 86400000,
            enabled: !!viewAddress,
        });


        //Pagination and filtration

        // Apply filter for compressed NFTs first
        let filteredTokens = showCompressed
            ? tokens
            : tokens.filter((t) => t.type !== "Compressed NFT");

        // Apply media type filter (video / audio / image / all)
        if (mediaFilter !== "all") {
            filteredTokens = filteredTokens.filter((t) => {
                const mt = (t.mediaType || "").toLowerCase();
                if (mediaFilter === "video") return mt === "video";
                if (mediaFilter === "audio") return mt === "audio";
                if (mediaFilter === "image") return mt === "image";
                return true;
            });
        }


        if (searchTerm.trim()) {
            const lower = searchTerm.toLowerCase().trim();
            filteredTokens = filteredTokens.filter((t) =>
                (t.name || "").toLowerCase().includes(lower)
            );
        }

    const audioTokens = useMemo(() => {
        return filteredTokens
            .filter((t) => (t?.mediaType || "").toLowerCase() === "audio")
            .filter((t) => !!t.audioUrl)
            .slice()
            .sort((a, b) => safeName(a).localeCompare(safeName(b)));
    }, [filteredTokens]);

    const audioCurrent = audioTokens[audioActiveIndex] || null;

    useEffect(() => {
        if (!audioTokens.length) {
            setAudioActiveIndex(0);
            return;
        }
        if (audioActiveIndex > audioTokens.length - 1) setAudioActiveIndex(0);
    }, [audioTokens, audioActiveIndex]);

    useEffect(() => {
        const el = audioRef.current;
        if (!el) return;
        if (!audioCurrent?.audioUrl) return;
        el.load();
        el.play().catch(() => {});
    }, [audioCurrent?.audioUrl]);



    const videoTokens = useMemo(() => {
        return filteredTokens
            .filter((t) => (t?.mediaType || "").toLowerCase() === "video")
            .filter((t) => !!t.animationUrl)
            .slice()
            .sort((a, b) => safeName(a).localeCompare(safeName(b)));
    }, [filteredTokens]);

    const videoCurrent = videoTokens[videoActiveIndex] || null;

    useEffect(() => {
        if (!videoTokens.length) {
            setVideoActiveIndex(0);
            return;
        }
        if (videoActiveIndex > videoTokens.length - 1) setVideoActiveIndex(0);
    }, [videoTokens, videoActiveIndex]);

    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        if (!videoCurrent?.animationUrl) return;

        // Reload + attempt autoplay (may be blocked)
        el.load();
        el.play().catch(() => {});
    }, [videoCurrent?.animationUrl]);

    const mapDASAssetToToken = async (asset) => {
        const assetId = asset?.id || null;
        if (!assetId) return null;

        const jsonUri = asset?.content?.json_uri || null;

        // Best-effort fields from DAS (fast path, no extra fetch)
        let metadata = asset?.content?.metadata || null;
        let imageUrl = asset?.content?.links?.image || metadata?.image || null;
        let externalUrl = metadata?.external_url || asset?.content?.links?.external || null;

        // Collection/grouping (DAS often uses `grouping`)
        const collectionKey =
            asset?.grouping?.find((g) => g?.group_key === "collection")?.group_value ||
            asset?.collection?.key ||
            null;

        let attributes = metadata?.attributes || [];

        // If we’re missing important stuff, hydrate from json_uri (slow path)
        const needsHydrate =
            !!jsonUri &&
            (
                !metadata ||
                !imageUrl ||
                !attributes ||
                (Array.isArray(attributes) && attributes.length === 0) ||

                // ✅ NEW: hydrate if we’re missing media info
                (
                    !metadata?.animation_url &&
                    !(
                        metadata?.properties &&
                        Array.isArray(metadata.properties.files) &&
                        metadata.properties.files.length
                    ) &&
                    !(
                        Array.isArray(asset?.content?.files) &&
                        asset.content.files.length
                    )
                )
            );
        if (needsHydrate) {
            const hydrated = await fetchJsonCached(jsonUri);
            if (hydrated) {
                // ✅ Merge so we keep DAS fields but also gain animation_url/files/category
                metadata = metadata
                    ? {
                        ...hydrated,
                        ...metadata,
                        properties: {
                            ...(hydrated.properties || {}),
                            ...(metadata.properties || {}),
                            files: [
                                ...((hydrated.properties && Array.isArray(hydrated.properties.files)) ? hydrated.properties.files : []),
                                ...((metadata.properties && Array.isArray(metadata.properties.files)) ? metadata.properties.files : []),
                            ],
                        },
                    }
                    : hydrated;

                imageUrl = imageUrl || hydrated?.image || null;
                externalUrl = externalUrl || hydrated?.external_url || null;
                attributes =
                    (attributes && attributes.length ? attributes : hydrated?.attributes) || [];
            }
        }

// --- Media parsing (DAS + hydrated json_uri) ---

// DAS sometimes provides files here (with mime/type)
        const dasFiles = Array.isArray(asset?.content?.files) ? asset.content.files : [];

// Metadata files (classic token-metadata)
        const metaFiles =
            (metadata?.properties && Array.isArray(metadata.properties.files))
                ? metadata.properties.files
                : [];

        const allFiles = [...dasFiles, ...metaFiles];

        const getMime = (f) => ((f?.mime || f?.type || "") + "").toLowerCase();
        const getUri = (f) => f?.uri || f?.url || null;

        const imageFile = allFiles.find((f) => getMime(f).startsWith("image/")) || null;
        const videoFile = allFiles.find((f) => getMime(f).startsWith("video/")) || null;
        const audioFile = allFiles.find((f) => getMime(f).startsWith("audio/")) || null;

        let animationUrl =
            metadata?.animation_url ||
            getUri(videoFile) ||
            null;

        let audioUrl =
            getUri(audioFile) ||
            null;

// ✅ If animation_url is actually audio, treat it as audio
        if (animationUrl && !audioUrl) {
            const lower = String(animationUrl).toLowerCase();
            if (lower.includes("ext=mp3") || lower.endsWith(".mp3") || lower.endsWith(".wav") || lower.endsWith(".m4a") || lower.endsWith(".ogg")) {
                audioUrl = animationUrl;
                animationUrl = null;
            }
        }

// Use cover image even when video/audio exists
        const coverImage =
            imageUrl ||
            getUri(imageFile) ||
            null;

        const categoryRaw = (
            metadata?.properties?.category ||
            metadata?.category ||
            asset?.content?.metadata?.properties?.category ||
            asset?.content?.metadata?.category ||
            ""
        ).toLowerCase();

// Determine mediaType
        let mediaCategory = "";
        if (animationUrl || categoryRaw === "video" || videoFile) mediaCategory = "video";
        else if (audioUrl || categoryRaw === "audio" || audioFile) mediaCategory = "audio";
        else if (coverImage || categoryRaw === "image" || imageFile) mediaCategory = "image";


        // Type: compressed vs regular (still all from DAS)
        const iface = asset?.interface || "";
        const isCompressed = !!asset?.compression?.compressed;

        let tokenType = "NFT"; // Legacy default
        if (isCompressed || iface === "MplBubblegumV2") tokenType = "Compressed NFT";
        else if (iface === "MplCoreAsset") tokenType = "Core NFT";
        else tokenType = "NFT"; // legacy token-metadata bucket

        return {
            mint: assetId,
            amount: 1,
            type: tokenType,
            name: metadata?.name || asset?.content?.metadata?.name || "Unknown",
            description: metadata?.description || "",
            symbol: metadata?.symbol || asset?.content?.metadata?.symbol || "Unknown",
            uri: jsonUri || "No Metadata URI",
            collectionKey,
            image: coverImage || "No image URL",
            external_url: externalUrl || null,
            animationUrl: animationUrl || null,
            audioUrl: audioUrl || null,
            mediaType: mediaCategory,
            attributes: Array.isArray(attributes) ? attributes : [],
        };
    };

    const handleLoadMoreAssets = async () => {
        // if (!viewAddress || dasLoadingMore || !dasHasMore) return;

        setDasLoadingMore(true);

        try {
            const originalAddress = String(viewAddress).trim();
            let ownerAddress = originalAddress;

            // keep SNS behavior consistent with fetchUserTokens
            if (originalAddress.toLowerCase().endsWith(".sol")) {
                ownerAddress = await getPublicKeyFromSolDomain(originalAddress);
            }

            const rpcEndpoint = process.env.REACT_APP_RPC_ENDPOINT;
            if (!rpcEndpoint) throw new Error("RPC endpoint is not configured.");

            const fetchDASPage = async (page) => {
                const body = {
                    jsonrpc: "2.0",
                    id: "get-assets-by-owner",
                    method: "getAssetsByOwner",
                    params: {
                        ownerAddress,
                        page,
                        limit: DAS_PAGE_SIZE,
                    },
                };

                const res = await fetch(rpcEndpoint, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                });

                const json = await res.json();
                if (json?.error) {
                    console.error("[DAS] getAssetsByOwner error:", json.error);
                    throw new Error(json.error.message || "DAS getAssetsByOwner failed");
                }

                return json?.result?.items || [];
            };

            // Reuse your existing mapper via closure (it exists in this component scope)
            // Reuse your existing streaming + dedupe approach:
            const existing = queryClient.getQueryData(["tokens", viewAddress]) || [];
            const uniqueTokensMap = new Map(existing.filter(t => t?.mint).map(t => [t.mint, t]));

            const pushToken = (token) => {
                if (!token || !token.mint) return;
                if (!uniqueTokensMap.has(token.mint)) {
                    uniqueTokensMap.set(token.mint, token);
                    queryClient.setQueryData(["tokens", viewAddress], Array.from(uniqueTokensMap.values()));
                }
            };

            // Load ONE more page per click (simple & safe UX)
            const page = dasNextPage;
            const items = await fetchDASPage(page);

            if (!items.length) {
                // No more pages
                setDasHasMore(false);
                return;
            }

            const CONCURRENCY = 12;
            await runPoolStream(CONCURRENCY, items, mapDASAssetToToken, (token) => pushToken(token));

            // If we got a full page, keep offering "Load more"
            const stillHasMore = items.length === DAS_PAGE_SIZE;

            const merged = Array.from(uniqueTokensMap.values());

            // final cache write
            const cacheKey = `tokens_${ownerAddress}`;
            localStorage.setItem(
                cacheKey,
                JSON.stringify({
                    data: merged,
                    timestamp: Date.now(),
                    dasPaging: {
                        hasMore: stillHasMore,
                        nextPage: page + 1,
                        pageSize: DAS_PAGE_SIZE,
                    },
                })
            );

            setDasHasMore(stillHasMore);
            setDasNextPage(page + 1);
        } catch (e) {
            console.error("Load more assets failed:", e);
        } finally {
            setDasLoadingMore(false);
        }
    };


    const indexOfLastToken = currentPage * tokensPerPage;
        const indexOfFirstToken = indexOfLastToken - tokensPerPage;
        const currentTokens = filteredTokens.slice(indexOfFirstToken, indexOfLastToken);
        const totalPages = Math.ceil(filteredTokens.length / tokensPerPage);

        const getVisiblePages = () => {
            const pageWindow = 5;
            const startPage = Math.max(1, currentPage - Math.floor(pageWindow / 2));
            const endPage = Math.min(totalPages, startPage + pageWindow - 1);
            return Array.from({length: endPage - startPage + 1}, (_, i) => startPage + i);
        };
        const visiblePages = getVisiblePages();

        const paginate = (pageNumber) => {
            if (pageNumber >= 1 && pageNumber <= totalPages) {
                setCurrentPage(pageNumber);
            }
        };




        // --- Burn Functions ---

        const handleBurn = async (token) => {
            if (!wallet || !wallet.publicKey) {
                alert("Wallet not connected.");
                return;
            }
            if (!window.confirm(`Are you sure you want to burn ${token.name}?`)) return;

            setBurnInfo({status: "Burn in progress...", tokenName: token.name});
            try {
                let txId;
                if (token.type === "NFT") {
                    txId = await burnDigitalAsset(token);
                } else if (token.type === "Core NFT") {
                    txId = await burnCoreAsset(token);
                } else if (token.type === "Compressed NFT") {
                    txId = await burnCompressedAsset(token);
                }
                localStorage.removeItem(`tokens_${viewAddress}`);
                queryClient.invalidateQueries(["tokens", viewAddress]);
                setBurnInfo({status: "Burn successful", tokenName: token.name, txId});
                alert("Token burned successfully.");
            } catch (error) {
                console.error("Error burning token:", error);
                setBurnInfo({status: "Burn failed", tokenName: token.name, error: error.message});
                alert("Burn failed. Please try again.");
            }
        };

        // Burn for digital assets using mpl-token-metadata.
        const burnDigitalAsset = async (token) => {
            const umi = createUmi(process.env.REACT_APP_RPC_ENDPOINT).use(mplTokenMetadata());
            umi.use(walletAdapterIdentity(wallet));
            const mint = publicKey(token.mint);
            const owner = wallet.publicKey;
            let burnInstruction;
            if (token.collectionKey) {
                burnInstruction = await burnV1(umi, {
                    mint,
                    authority: owner,
                    tokenOwner: owner,
                    collectionMetadata: findMetadataPda(umi, {mint: publicKey(token.collectionKey)}),
                    tokenStandard: TokenStandard.NonFungible,
                });
            } else {
                burnInstruction = await burnV1(umi, {
                    mint,
                    authority: owner,
                    tokenOwner: owner,
                    tokenStandard: TokenStandard.NonFungible,
                });
            }
            const txId = await sendTxWithConfirmation(burnInstruction, umi);
            console.log("Digital asset burn transaction sent:", txId);
            return txId;
        };

// imports you should already have somewhere above:
// import { createUmi } from '@metaplex-foundation/umi-bundle-defaults';
// import { publicKey } from '@metaplex-foundation/umi';
// import { walletAdapterIdentity } from '@metaplex-foundation/umi-signer-wallet-adapters';
// import { mplBubblegum, getAssetWithProof, burnV2 } from '@metaplex-foundation/mpl-bubblegum';
// import { dasApi } from '@metaplex-foundation/digital-asset-standard-api';

        const burnCompressedAsset = async (token) => {
            const endpoint = process.env.REACT_APP_RPC_ENDPOINT;

            // 1) Umi + wallet + Bubblegum
            const umi = createUmi(endpoint)
                .use(walletAdapterIdentity(wallet))
                .use(mplBubblegum());

            // 2) 🔧 Patch rpc.call AFTER all .use() calls so we intercept the final rpc
            const rawCall = umi.rpc.call.bind(umi.rpc);
            umi.rpc.call = (...args) => {
                const [method, params, ...rest] = args;

                let normalizedParams;
                if (params === undefined) {
                    normalizedParams = [];
                } else if (Array.isArray(params)) {
                    normalizedParams = params;
                } else {
                    normalizedParams = [params]; // object -> [object]
                }

                return rawCall(method, normalizedParams, ...rest);
            };

            // 3) Asset id for the compressed NFT.
            //    You said you stored this; it MUST be a base58 string.
            const assetIdStr = token.assetId || token.id || token.mint;
            if (typeof assetIdStr !== 'string' || !assetIdStr) {
                console.error('burnCompressedAsset: missing asset id on token:', token);
                throw new Error('Compressed NFT is missing a valid asset id string.');
            }

            const assetId = publicKey(assetIdStr);

            // 4) EXACT docs pattern for getAssetWithProof
            const assetWithProof = await getAssetWithProof(umi, assetId, {
                truncateCanopy: true,
            });

            // 5) leafOwner must be a PublicKey (current owner)
            const leafOwner = publicKey(wallet.publicKey.toBase58());

            const burnBuilder = burnV2(umi, {
                ...assetWithProof,
                leafOwner,
            });

            // You can swap this to your sendTxWithConfirmation if you want.
            const txSig = await burnBuilder.sendAndConfirm(umi);
            console.log('Compressed asset burn transaction sent:', txSig);

            return txSig;
        };


        // Burn for core assets using mpl-core.
        const burnCoreAsset = async (token) => {
            const umi = createUmi(process.env.REACT_APP_RPC_ENDPOINT);
            umi.use(walletAdapterIdentity(wallet));
            const asset = await fetchAsset(umi, publicKey(token.mint));
            let collection = undefined;
            const collectionId = collectionAddress(asset);
            if (collectionId) {
                collection = await fetchCollection(umi, collectionId);
            }
            const burnInstruction = await burn(umi, {
                asset,
                collection,
            });
            const txId = await sendTxWithConfirmation(burnInstruction, umi);
            console.log("Core asset burn transaction sent:", txId);
            return txId;
        };

        // --- End Burn Functions ---

        return (
            <div className='wallet-explorer-outer-root'>
                <div className={`wallet-explorer-root theme-${styleMode}`}>
                    <h1>Filters & Style</h1>
                    <div className="style-switcher">
                        <span className="style-switcher-label">Style:</span>

                        {[...FREE_STYLES, ...PREMIUM_STYLES].map((style) => {
                            const isUnlocked = unlockedStyles.includes(style.id);
                            const isActive = styleMode === style.id;

                            return (
                                <button
                                    key={style.id}
                                    type="button"
                                    className={
                                        isActive
                                            ? "style-switcher-button active"
                                            : "style-switcher-button"
                                    }
                                    disabled={!isUnlocked}
                                    onClick={() => isUnlocked && handleStyleChange(style.id)}
                                    title={
                                        isUnlocked
                                            ? style.label
                                            : "Locked – purchase in Style Shop"
                                    }
                                >
                                    {style.label}
                                    {!isUnlocked && " 🔒"}
                                </button>
                            );
                        })}
                    </div>


                    <p>While loading, NFTs are sorted in the order that they appear.</p>
                    <p>When loading is 100% complete, wallet contents get sorted by collection.</p>


                    <div className="cnft-filter-group" style={{marginBottom: 12}}>
                        <p>
                            <label className="cnft-filter-option" style={{marginRight: 16}}>
                                <input
                                    type="radio"
                                    name="cnftFilter"
                                    checked={showCompressed}
                                    onChange={() => setShowCompressed(true)}
                                />{" "}
                                Show all tokens
                            </label>
                        </p>
                        <p>
                            <label className="cnft-filter-option">
                                <input
                                    type="radio"
                                    name="cnftFilter"
                                    checked={!showCompressed}
                                    onChange={() => setShowCompressed(false)}
                                />{" "}
                                Hide Compressed NFTs
                            </label>
                        </p>
                    </div>


                    <div className="media-filter-group" style={{marginBottom: 12}}>
                        <span style={{marginRight: 8}}>Media Filter:</span>
                        <p>
                            <label className="media-filter-option">
                                <input
                                    type="radio"
                                    name="mediaFilter"
                                    checked={mediaFilter === "all"}
                                    onChange={() => setMediaFilter("all")}
                                />{" "}
                                Show All
                            </label>
                        </p>
                        <p>
                            <label className="media-filter-option">
                                <input
                                    type="radio"
                                    name="mediaFilter"
                                    checked={mediaFilter === "video"}
                                    onChange={() => setMediaFilter("video")}
                                />{" "}
                                Show Video
                            </label>
                        </p>
                        <p>


                            <label className="media-filter-option">
                                <input
                                    type="radio"
                                    name="mediaFilter"
                                    checked={mediaFilter === "audio"}
                                    onChange={() => setMediaFilter("audio")}
                                />{" "}
                                Show Audio
                            </label>
                        </p>
                        <p>


                            <label className="media-filter-option">
                                <input
                                    type="radio"
                                    name="mediaFilter"
                                    checked={mediaFilter === "image"}
                                    onChange={() => setMediaFilter("image")}
                                />{" "}
                                Show Images
                            </label>
                        </p>
                    </div>
                    <div className="name-search-bar">
                        <label>Filter by name:</label>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Start typing an NFT name…"
                        />
                    </div>

                    <div>
                    <button
                        onClick={async () => {
                            setIsRefreshing(true);

                            // Always clear by what the UI is using
                            localStorage.removeItem(`tokens_${viewAddress}`);

                            // If it's a .sol domain, also clear the resolved wallet cache
                            if (typeof viewAddress === "string" && viewAddress.toLowerCase().endsWith(".sol")) {
                                try {
                                    const resolved = await getPublicKeyFromSolDomain(viewAddress);
                                    localStorage.removeItem(`tokens_${resolved}`);
                                } catch (e) {
                                    // ignore resolution errors during refresh
                                }
                            }

                            queryClient.setQueryData(["tokens", viewAddress], undefined);
                            setCurrentPage(1);
                            await refetch();
                            setIsRefreshing(false);
                        }}
                        disabled={isRefreshing || isLoading}
                    >
                        {isRefreshing || isLoading ? "Refreshing Wallet Data..." : "Refresh Connected Wallet Data"}
                    </button>
                    </div>
                    <div className="view-mode-tabs">
                        <p>NFT Player Mode:</p>
                        <button
                            type="button"
                            className={activeTab === "explorer" ? "active" : ""}
                            onClick={() => setActiveTab("explorer")}
                        >
                            Explorer
                        </button>
                        <button
                            type="button"
                            className={activeTab === "audio" ? "active" : ""}
                            onClick={() => setActiveTab("audio")}
                        >
                            NFT Audio Player ({audioTokens.length})
                        </button>
                        <button
                            type="button"
                            className={activeTab === "video" ? "active" : ""}
                            onClick={() => setActiveTab("video")}
                        >
                            Video Player ({videoTokens.length})
                        </button>
                    </div>

                    {/* Burn transaction information */}
                    {burnInfo && (
                        <div className="burn-info">
                            <p>
                                <strong>Burn Status: </strong> {burnInfo.status}
                            </p>
                            <p>
                                <strong>Token: </strong> {burnInfo.tokenName}
                            </p>
                            {burnInfo.txId && (
                                <p>
                                    <strong>Transaction: </strong>
                                    <a
                                        href={`https://solscan.io/tx/${burnInfo.txId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{color: "#88ff88"}}
                                    >
                                        {burnInfo.txId}
                                    </a>
                                </p>
                            )}
                            {burnInfo.error && (
                                <p>
                                    <strong>Error: </strong> {burnInfo.error}
                                </p>
                            )}
                        </div>
                    )}


                    {isLoading || isRefreshing &&
                    <p>Refreshing Wallet Contents. This may take a few minutes to complete. This message will disappear
                        when finished.
                        Please consider leaving a tip to support the project. Thanks for using the NFT Viewer!

                    </p>
                    }

                    {(isLoading || isRefreshing) && (
                        <div className="loading-indicator">

                    <span>Loading Wallet Contents. This may take a few minutes to complete. This message will disappear when finished.
                Please consider leaving a tip to support the project. Thanks for using the NFT Viewer!</span>
                            <div className="loading-spinner"/>
                        </div>
                    )}

                    {!isLoading && !isRefreshing && tokens.length >= 990 && (
                        <div style={{ margin: "12px 0" }}>
                            <button onClick={handleLoadMoreAssets} disabled={dasLoadingMore}>
                                {dasLoadingMore ? "Loading more assets..." : "Load more assets?"}
                            </button>
                        </div>
                    )}




                    {activeTab === "audio" ? (
                        <div style={{ display: activeTab === "audio" ? "block" : "none" }}>
                            <AudioNFTPlayerComponent
                                viewAddress={viewAddress}
                                isLoading={isLoading}
                                isRefreshing={isRefreshing}
                                tokens={audioTokens}
                                isActive={activeTab === "audio"}
                            />
                        </div>
                    ) : activeTab === "video" ? (
                        <div style={{ display: activeTab === "video" ? "block" : "none" }}>
                            <VideoNFTPlayerComponent
                                viewAddress={viewAddress}
                                isLoading={isLoading}
                                isRefreshing={isRefreshing}
                                tokens={videoTokens}
                                isActive={activeTab === "video"}
                            />
                        </div>
                            ) : (
                        <>
                            <div className="pagination">
                                <button onClick={() => paginate(1)} disabled={currentPage === 1}>
                                    {"<<"}
                                </button>

                                <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
                                    Previous
                                </button>

                                {visiblePages.map((number) => (
                                    <button
                                        key={number}
                                        onClick={() => paginate(number)}
                                        className={currentPage === number ? "active" : ""}
                                    >
                                        {number}
                                    </button>
                                ))}

                                <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>
                                    Next
                                </button>

                                <button onClick={() => paginate(totalPages)} disabled={currentPage === totalPages}>
                                    {">>"}
                                </button>
                            </div>

                            {isLoading && <p>Loading tokens...</p>}

                    {filteredTokens.length > 0 ? (
                        <div className="token-list-container">
                            {currentTokens.map((token, index) => (
                                <div
                                    key={token.mint || index}
                                    className="token-list-item-outer"
                                    ref={(el) => {
                                        if (el) {
                                            cardRefs.current[token.mint] = el;
                                        }
                                    }}
                                >
                                    <div className="pokedex-light-container">
                                        <div className="pokedex-light"/>
                                    </div>
                                    <div className="token-name-container">

                                        <h1 className="token-name-item"><strong>{token.name}</strong></h1>
                                    </div>

                                    <div key={index} className="token-list-item">
                                        {/*<div className="description-item">*/}

                                        {/*</div>*/}
                                        {(token.type === "Compressed NFT" || token.type === "Core NFT" || token.type === "NFT") &&
                                        (token.animationUrl || token.audioUrl) ? (
                                            <>
                                                <div className='media-container'>

                                                    {token.image !== "No image URL" && (
                                                        <div className="token-item-image-container">
                                                            <TokenImage
                                                                image={token.image}
                                                                name={token.name}
                                                                setModalImage={setModalImage}
                                                            />
                                                        </div>
                                                    )}

                                                    {token.animationUrl && (
                                                        <div className="token-item-image-container">
                                                            <video
                                                                className="token-item-image"
                                                                src={token.animationUrl}
                                                                controls
                                                                loop
                                                                muted
                                                            />
                                                        </div>
                                                    )}
                                                    {!token.animationUrl && token.audioUrl && (
                                                        <div className="token-item-image-container">
                                                            <audio
                                                                className="token-item-audio"
                                                                src={token.audioUrl}
                                                                controls
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        ) : token.image !== "No image URL" ? (
                                            <div className="token-item-image-container">
                                                <TokenImage
                                                    image={token.image}
                                                    name={token.name}
                                                    setModalImage={setModalImage}
                                                />
                                            </div>
                                        ) : (
                                            <p>No image available</p>
                                        )}


                                        <div className="token-item-description-container">
                                            <div className="description-item">
                                                <strong>Name: </strong>{token.name}
                                            </div>
                                            <div className="description-item">
                                                {/*<strong>Amount: </strong>{token.token_amount}*/}
                                                <strong>Type: </strong> {token.type}{" "}

                                            </div>
                                            <div className="description-item">
                                                <strong>Media Type: </strong> {token.mediaType}
                                            </div>

                                            <div className="description-item">

                                                <strong>Symbol: </strong> {token.symbol}
                                            </div>
                                            {
                                                token.external_url && <>
                                                    <div className="description-item">
                                                        <strong>External URL:</strong>{token.external_url}
                                                    </div>
                                                </>
                                            }

                                            <div className="description-item">
                                                <strong>Description: </strong> {token.description}
                                            </div>
                                            <div className="description-item">
                                                {token.collectionKey && (
                                                    <>
                                                        <strong>Collection: </strong>
                                                        <a
                                                            href={`https://solscan.io/token/${token.collectionKey}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {token.collectionKey}
                                                        </a>
                                                        <br/>
                                                    </>
                                                )}
                                            </div>
                                            <div className="description-item">
                                                <strong>Mint Address: </strong>
                                                <a
                                                    href={`https://solscan.io/token/${token.mint}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {token.mint}
                                                </a>
                                            </div>
                                            <div className="description-item">
                                                <strong>Metadata URI: </strong>
                                                <a href={token.uri} target="_blank" rel="noopener noreferrer">
                                                    {token.uri}
                                                </a>
                                            </div>

                                            {Array.isArray(token.attributes) && token.attributes.length > 0 && (
                                                <div className="description-item">
                                                    <strong>Attributes:</strong>
                                                    <table className="token-attributes-table">
                                                        <thead>
                                                        <tr>
                                                            <th>Trait</th>
                                                            <th>Value</th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        {token.attributes.map((attr, idx) => (
                                                            <tr key={idx}>
                                                                <td>{attr.trait_type || attr.traitType || "-"}</td>
                                                                <td>{String(attr.value ?? "")}</td>
                                                            </tr>
                                                        ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}


                                            {canBurnThisWallet && (
                                                <>
                                                    <div className="ownership-badge-container">
                                                        <h5>⭐ Digital Asset Owner</h5>
                                                    </div>
                                                    <div className="burn-button-container">
                                                        <h4>Burn this digital asset. Check the metadata to ensure that
                                                            it isn't important!</h4>
                                                        <button id="burn-button" onClick={() => handleBurn(token)}>
                                                            Burn
                                                        </button>
                                                    </div>
                                                </>
                                            )}

                                            <div className="token-share-bar">
                                                <span className="token-share-label">Share card:</span>
                                            </div>
                                            <div>
                                                <button
                                                    className="token-share-button"
                                                    onClick={() => handleShareTokenCard(token, "twitter")}
                                                    disabled={sharingTokenMint === token.mint}
                                                >
                                                    <span className="token-share-pill">X</span>
                                                    Twitter
                                                </button>

                                                <button
                                                    className="token-share-button"
                                                    onClick={() => handleShareTokenCard(token, "facebook")}
                                                    disabled={sharingTokenMint === token.mint}
                                                >
                                                    <span className="token-share-pill">f</span>
                                                    Facebook
                                                </button>

                                                <button
                                                    className="token-share-button"
                                                    onClick={() => handleShareTokenCard(token, "instagram")}
                                                    disabled={sharingTokenMint === token.mint}
                                                >
                                                    <span className="token-share-pill">IG</span>
                                                    Instagram
                                                </button>

                                                <button
                                                    className="token-share-button"
                                                    onClick={() => handleShareTokenCard(token, "threads")}
                                                    disabled={sharingTokenMint === token.mint}
                                                >
                                                    <span className="token-share-pill">@</span>
                                                    Threads
                                                </button>

                                                {/* --- NEW BUTTONS BELOW --- */}

                                                <button
                                                    className="token-share-button"
                                                    onClick={() => handleDownloadTokenCard(token)}
                                                    disabled={sharingTokenMint === token.mint}
                                                >
                                                    <span className="token-share-pill">↓</span>
                                                    Download
                                                </button>

                                                <button
                                                    className="token-share-button"
                                                    onClick={() => handleCopyTokenCard(token)}
                                                    disabled={sharingTokenMint === token.mint}
                                                >
                                                    <span className="token-share-pill">⧉</span>
                                                    Copy Card
                                                </button>


                                            </div>
                                            <div>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        navigate(`/token/${token.mint}`, {
                                                            state: {token, owner: viewAddress},
                                                        })
                                                    }
                                                >
                                                    View Detail Page
                                                </button>
                                            </div>


                                        </div>


                                    </div>
                                </div>

                            ))}

                            <div className="pagination">
                                <button
                                    onClick={() => paginate(1)}
                                    disabled={currentPage === 1}
                                >
                                    {"<<"}
                                </button>

                                <button
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </button>

                                {visiblePages.map((number) => (
                                    <button
                                        key={number}
                                        onClick={() => paginate(number)}
                                        className={currentPage === number ? "active" : ""}
                                    >
                                        {number}
                                    </button>
                                ))}

                                <button
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>

                                <button
                                    onClick={() => paginate(totalPages)}
                                    disabled={currentPage === totalPages}
                                >
                                    {">>"}
                                </button>
                            </div>
                        </div>
                    ) : (
                        (isLoading || isRefreshing) ? null : (
                            <p>
                                No tokens found for this wallet
                                {showCompressed ? "" : " (with compressed NFTs hidden)"}.
                            </p>
                        )
                    )}

                    {error && (
                        <p style={{whiteSpace: "pre-wrap"}}>
                            Error loading wallet: {String(error.message || error)}
                        </p>
                    )}

                            {filteredTokens.length > 0 ? (
                                <div className="token-list-container">
                                    {/* keep your existing currentTokens.map(...) block exactly as-is */}
                                    {currentTokens.map((token, index) => (
                                        /* ... your existing token card markup ... */
                                        <div key={token.mint || index}>{/* unchanged */}</div>
                                    ))}

                                    {/* keep your existing SECOND pagination block here if you want it */}
                                </div>
                            ) : (isLoading || isRefreshing) ? null : (
                                <p>
                                    No tokens found for this wallet
                                    {showCompressed ? "" : " (with compressed NFTs hidden)"}.
                                </p>
                            )}
                        </>
                    )}


                    {modalImage && (
                        <ImageModal
                            image={modalImage.image}
                            name={modalImage.name}
                            onClose={() => setModalImage(null)}
                        />
                    )}
                </div>
            </div>
        );

}

export default WalletExplorer;
